import { useCallback, useEffect, useRef, useState } from 'react';
import { forgetGuestImage, loadGuestImage, peekGuestImage } from './guestImageCache';

export interface AuthedImageState {
  src: string | null;
  /** Laden ist gescheitert (404, 5xx, abgelaufener Zugang) — kein endloser Lade-Schimmer. */
  failed: boolean;
  /** Neuer Versuch nach einem Fehlschlag, etwa über „Erneut laden“. */
  retry: () => void;
  /**
   * Für `onError` am `<img>`: die geladene Adresse taugt nicht mehr. Einmal wird neu geladen,
   * danach gilt das Bild als gescheitert — kein Kreislauf bei Bytes, die kein Bild sind.
   */
  broken: () => void;
}

/**
 * Lädt ein auth-geschütztes Bild mit dem Gast-Token und gibt eine Object-URL zurück. Ein
 * Browser-`<img src>` kann keinen Authorization-Header schicken — der gast-gescopte Endpunkt
 * verlangt ihn aber. Die Bilder liegen im gemeinsamen Zwischenspeicher: ein schon gesehenes Bild
 * steht sofort da, ohne neuen Abruf (der Speicher gibt die Object-URLs frei, nicht diese Anzeige).
 */
export function useAuthedImageState(
  url: string | null,
  token: string | undefined,
): AuthedImageState {
  const [attempt, setAttempt] = useState(0);
  const reloadedAfterBreak = useRef(false);
  const [state, setState] = useState(() => ({
    src: url ? peekGuestImage(url) : null,
    failed: false,
  }));

  useEffect(() => {
    reloadedAfterBreak.current = false;
  }, [url]);

  useEffect(() => {
    if (!url || !token) {
      setState({ src: null, failed: false });
      return;
    }
    const cached = peekGuestImage(url);
    setState({ src: cached, failed: false });
    if (cached) return;
    let cancelled = false;
    loadGuestImage(url, token)
      .then((src) => {
        if (!cancelled) setState({ src, failed: false });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // Nur der Status, nie die Adresse: sie trägt Buchungs- und Datei-IDs.
        console.warn('[guest-portal] image failed', {
          status: error instanceof Error ? error.message : 'network',
        });
        setState({ src: null, failed: true });
      });
    return () => {
      cancelled = true;
    };
  }, [url, token, attempt]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);
  const broken = useCallback(() => {
    if (!url) return;
    forgetGuestImage(url);
    console.warn('[guest-portal] image unreadable');
    if (reloadedAfterBreak.current) {
      setState({ src: null, failed: true });
      return;
    }
    reloadedAfterBreak.current = true;
    setAttempt((value) => value + 1);
  }, [url]);

  return { ...state, retry, broken };
}

export function useAuthedImage(url: string | null, token: string | undefined): string | null {
  return useAuthedImageState(url, token).src;
}
